import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'sonner';
import { t } from 'i18next';
import { Clock, ArrowLeft } from 'lucide-react';
import { RadixButton, RadixCard, RadixOtpInput, RadixSpinner } from '@/components/radix';
import { verifyStorePasswordResetOtpV3Api } from '@/utils/Api/AuthV3';
import type { AxiosError } from 'axios';
import { useLoginV3Flow } from './LoginV3FlowProvider';

export default function LoginV3ResetOtpCard() {
    const { state, goBackInFlow, requestPasswordResetOtp, onResetOtpVerified } = useLoginV3Flow();
    const email = state.reset.email;
    const [timeLeft, setTimeLeft] = useState(90);
    const [isResendActive, setIsResendActive] = useState(false);
    const [isPending, setIsPending] = useState(false);

    const formik = useFormik({
        initialValues: { code: ['', '', '', '', '', ''] as string[] },
        validationSchema: Yup.object({
            code: Yup.array()
                .of(
                    Yup.string()
                        .matches(/^[0-9]$/)
                        .required(),
                )
                .length(6),
        }),
        onSubmit: async (values) => {
            const cod = values.code.join('');
            try {
                setIsPending(true);
                await verifyStorePasswordResetOtpV3Api({
                    email: email.trim(),
                    otp: cod,
                });
                toast.success(t('Common.LoginResetOtpSuccess'));
                onResetOtpVerified(cod);
            } catch (error) {
                const ax = error as AxiosError<{ detail?: string; msg?: string; message?: string }>;
                toast.error(
                    ax?.response?.data?.detail ||
                        ax?.response?.data?.msg ||
                        ax?.response?.data?.message ||
                        t('Common.LoginResetOtpInvalid'),
                );
            } finally {
                setIsPending(false);
            }
        },
    });

    useEffect(() => {
        const isComplete = formik.values.code.every((digit) => digit !== '');
        if (isComplete && !isPending) {
            formik.handleSubmit();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- submit only when OTP digits change
    }, [formik.values.code]);

    useEffect(() => {
        let timer: ReturnType<typeof setInterval>;
        if (timeLeft > 0) {
            timer = setInterval(() => setTimeLeft((prev) => prev - 1), 1000);
        } else {
            setIsResendActive(true);
        }
        return () => clearInterval(timer);
    }, [timeLeft]);

    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const handleResendCode = async () => {
        await requestPasswordResetOtp({ setter: setIsPending, email });
        setTimeLeft(90);
        setIsResendActive(false);
        formik.resetForm();
    };

    return (
        <RadixCard className="!p-0 border !border-[#E6E1D9] shadow-xl rounded-md bg-white/95 backdrop-blur-md overflow-hidden">
            <div className="relative bg-white p-6 sm:p-8 pb-4 text-center border-b border-[#F0EDE8]">
                <button
                    type="button"
                    onClick={goBackInFlow}
                    aria-label={t('Common.Back')}
                    className="absolute left-4 top-8 p-2 text-gray-400 hover:text-primary-500 transition-colors bg-transparent border-none outline-none cursor-pointer"
                >
                    <ArrowLeft size={20} />
                </button>

                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight">{t('Common.ResOTPt')}</h1>
                <p className="text-gray-500 text-xs sm:text-sm mt-1">{t('Common.ResOtpDes1')}</p>
                <p className="text-primary-500 font-bold text-sm mt-1">{email}</p>
            </div>

            <div className="p-6 sm:p-8">
                <form onSubmit={formik.handleSubmit} className="flex flex-col items-center">
                    <div className="mb-6">
                        <RadixOtpInput
                            id="login-reset-otp"
                            name="code"
                            autoComplete="one-time-code"
                            value={formik.values.code.join('')}
                            length={6}
                            onChange={(value) => {
                                const digits = value
                                    .replace(/\D/g, '')
                                    .slice(0, 6)
                                    .split('');
                                const padded = Array.from({ length: 6 }, (_, index) => digits[index] || '');
                                void formik.setFieldValue('code', padded);
                            }}
                            inputClassName="w-12 h-14 sm:w-16 sm:h-20 text-center text-2xl font-bold bg-transparent border-[1px] border-solid border-[#E6E1D9] rounded-xl focus:border-primary-500 focus:bg-white focus:ring-4 focus:ring-primary-500/10 transition-all outline-none"
                        />
                    </div>

                    <div className="flex items-center justify-center min-h-[40px] mb-4">
                        {isResendActive ? (
                            <button
                                type="button"
                                onClick={handleResendCode}
                                className="text-primary-500 text-sm font-bold hover:underline cursor-pointer bg-transparent border-none outline-none"
                            >
                                {t('Common.LoginResetOtpResend')}
                            </button>
                        ) : (
                            <div className="flex items-center gap-2 text-gray-400 bg-gray-50 px-4 py-1.5 rounded-full border border-gray-100">
                                <Clock size={14} className="text-primary-400" />
                                <span className="text-xs font-mono font-bold">{formatTime(timeLeft)}</span>
                            </div>
                        )}
                    </div>

                    <RadixButton
                        type="submit"
                        variant="primary"
                        className="w-full !h-12 sm:!h-14 !rounded-xl !text-base font-bold shadow-lg shadow-primary-500/10 text-white"
                        disabled={formik.values.code.some((d) => d === '') || isPending}
                    >
                        {isPending ? <RadixSpinner size="sm" /> : t('Common.ResOTpSubBTN')}
                    </RadixButton>
                </form>
            </div>
        </RadixCard>
    );
}
